import { useEffect, useState } from "react";
import { HandHeart, Calendar, Flame, BookOpen, PlayCircle, Lock, Globe, Send } from "lucide-react";
import { api, BRAND } from "../lib/api";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { VeteranHousing } from "../components/site/VeteranHousing";

const VLOG_FALLBACK_THUMB = "https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=1200&q=70";

function youtubeEmbed(url) {
  if (!url) return null;
  // Channel handle? Just deep-link
  const idMatch = url.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{6,})/);
  if (idMatch) return `https://www.youtube.com/embed/${idMatch[1]}`;
  return null;
}

export default function Services() {
  const [schedule, setSchedule] = useState([]);
  const [vlog, setVlog] = useState([]);
  const [prayers, setPrayers] = useState([]);

  // Prayer form
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [request, setRequest] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [s, v, p] = await Promise.all([
        api.get("/services/schedule"),
        api.get("/vlog"),
        api.get("/prayers/public"),
      ]);
      setSchedule(s.data || []);
      setVlog(v.data || []);
      setPrayers(p.data || []);
    } catch {}
  };

  useEffect(() => {
    load();
    const hash = window.location.hash.slice(1);
    if (hash === "prayer" || hash === "veteran-housing") {
      setTimeout(() => document.getElementById(hash)?.scrollIntoView({behavior:"smooth"}), 400);
    }
  }, []);

  const submitPrayer = async (e) => {
    e.preventDefault();
    if (!request.trim()) return toast.error("Please share your prayer request");
    setBusy(true);
    try {
      await api.post("/prayers", { name: name || "Anonymous", email: email || null, request, is_public: isPublic });
      toast.success("Prayer received. We're standing with you.");
      setRequest(""); setName(""); setEmail("");
      load();
    } catch {
      toast.error("Could not submit right now.");
    } finally {
      setBusy(false);
    }
  };

  const kindIcon = { "service": Flame, "bible-study": BookOpen, "prayer": HandHeart };

  return (
    <div data-testid="page-services">
      {/* HERO */}
      <section className="container-narrow pt-16 pb-14">
        <div className="label-luxe">Services & Community</div>
        <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] mt-4 max-w-4xl">
          Gather. Pray. <span className="italic gold-text">Be transformed.</span>
        </h1>
        <p className="mt-6 text-white text-lg max-w-2xl leading-relaxed">
          Wherever you are in the world, you have a seat at this altar. Live services,
          prayer gatherings, mid-week study, and a 24/7 prayer wall.
        </p>
      </section>

      {/* LIVE EMBED */}
      <section className="container-narrow pb-16">
        <div className="rounded-[28px] overflow-hidden border border-white/10 glass-strong">
          <a href={BRAND.youtube} target="_blank" rel="noreferrer" data-testid="youtube-embed" className="block relative aspect-video group">
            <img
              src="https://images.unsplash.com/photo-1607390475878-9f924c2c7e0a?crop=entropy&cs=srgb&fm=jpg&w=1600&q=70"
              alt="Watch live"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-[#050814]/70 group-hover:bg-[#050814]/55 transition flex flex-col items-center justify-center text-white text-center px-6">
              <div className="w-20 h-20 rounded-full bg-[#D4AF37] text-[#050814] flex items-center justify-center shadow-2xl group-hover:scale-110 transition">
                <PlayCircle className="w-12 h-12" fill="currentColor" />
              </div>
              <div className="mt-5 text-xs sm:text-sm uppercase tracking-[0.35em] text-[#D4AF37] font-semibold">Watch · Live & Replays</div>
              <div className="font-serif text-3xl sm:text-4xl mt-2">@Thesurrendered1</div>
              <div className="text-white/90 text-sm mt-2">Tap to join the live stream on YouTube</div>
            </div>
          </a>
          <div className="p-6 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="font-serif text-xl">Live & Replays</div>
              <div className="text-sm text-white/80">Streaming weekly via YouTube — @Thesurrendered1</div>
            </div>
            <a href={BRAND.youtube} target="_blank" rel="noreferrer" data-testid="open-youtube" className="btn-fuchsia !py-2.5 !px-5 text-sm">
              <PlayCircle className="w-4 h-4" /> Open YouTube Channel
            </a>
          </div>
        </div>
      </section>

      {/* SCHEDULE */}
      <section className="container-narrow pb-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="label-luxe">Weekly Rhythm</div>
            <h2 className="font-serif text-4xl mt-2">Online Service Schedule</h2>
          </div>
          <Calendar className="w-7 h-7 text-[#D4AF37] hidden md:block" />
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {schedule.map((s) => {
            const Icon = kindIcon[s.kind] || Flame;
            return (
              <div key={s.id} className="card-luxe" data-testid={`schedule-${s.kind}`}>
                <Icon className="w-7 h-7 text-[#D4AF37]" />
                <div className="mt-4 text-xs uppercase tracking-[0.3em] text-[#F02078]">{s.day}</div>
                <h3 className="font-serif text-2xl mt-1">{s.title}</h3>
                <div className="text-white/80 text-sm mt-1">{s.time}</div>
                <p className="mt-4 text-white text-sm leading-relaxed">{s.description}</p>
                {s.youtube_url && (
                  <a href={s.youtube_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-5 text-sm font-semibold text-[#D4AF37] hover:text-[#ECC23D]">
                    <PlayCircle className="w-4 h-4" /> Join Live
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <VeteranHousing />

      {/* PRAYER WALL */}
      <section id="prayer" className="container-narrow py-20">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="label-luxe">Prayer Wall</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 leading-tight">
              Submit a <span className="fuchsia-text italic">prayer request</span>.
            </h2>
            <p className="mt-5 text-white leading-relaxed">
              Pastor Shalonda and the intercessory team will lift you up.
              Choose <em>private</em> for your eyes only, or <em>public</em> to be seen and prayed
              for by the community.
            </p>

            <form onSubmit={submitPrayer} className="mt-8 space-y-4 card-luxe">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="label-luxe">Name (optional)</label>
                  <input data-testid="prayer-name" value={name} onChange={(e)=>setName(e.target.value)} className="input-luxe mt-2" placeholder="Anonymous"/>
                </div>
                <div>
                  <label className="label-luxe">Email (optional)</label>
                  <input data-testid="prayer-email" type="email" value={email} onChange={(e)=>setEmail(e.target.value)} className="input-luxe mt-2" placeholder="you@email.com"/>
                </div>
              </div>
              <div>
                <label className="label-luxe">Your Prayer</label>
                <textarea data-testid="prayer-request" required value={request} onChange={(e)=>setRequest(e.target.value)} rows={5} className="input-luxe mt-2" placeholder="Share what's on your heart…"/>
              </div>
              <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-3">
                  {isPublic ? <Globe className="w-5 h-5 text-[#ECC23D]" /> : <Lock className="w-5 h-5 text-[#D91668]" />}
                  <div>
                    <div className="text-sm font-semibold">{isPublic ? "Public" : "Private"}</div>
                    <div className="text-xs text-white/80">{isPublic ? "Visible on the prayer wall" : "Pastoral team only"}</div>
                  </div>
                </div>
                <Switch data-testid="prayer-public-toggle" checked={isPublic} onCheckedChange={setIsPublic} />
              </div>
              <button data-testid="prayer-submit" disabled={busy} className="btn-gold w-full">
                <Send className="w-4 h-4" /> {busy ? "Submitting…" : "Submit Prayer"}
              </button>
            </form>
          </div>

          <div className="lg:col-span-7">
            <div className="label-luxe">From The Wall</div>
            <h3 className="font-serif text-3xl mt-3 mb-6">Recent prayers being lifted</h3>
            <div className="space-y-4 max-h-[640px] overflow-y-auto pr-2">
              {prayers.length === 0 && (
                <div className="card-luxe text-white/80 text-center py-12">
                  Be the first to share a prayer with the community.
                </div>
              )}
              {prayers.map((p)=>(
                <div key={p.id} className="card-luxe" data-testid="prayer-card">
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-serif text-lg">{p.name}</div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">Public</div>
                  </div>
                  <p className="mt-3 text-white/90 italic leading-relaxed">"{p.request}"</p>
                  <div className="mt-3 text-xs text-white/80">
                    {new Date(p.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* VLOG */}
      <section className="container-narrow py-20">
        <div className="label-luxe">Daily Life Vlog</div>
        <h2 className="font-serif text-4xl mt-3 mb-8">Behind the surrender — with Pastor Shalonda.</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {vlog.map((v)=>(
            <a key={v.id} href={v.youtube_url || BRAND.youtube} target="_blank" rel="noreferrer" data-testid="vlog-card" className="card-luxe block group">
              <div className="aspect-video rounded-2xl overflow-hidden mb-4 relative">
                <img src={v.thumbnail_url || VLOG_FALLBACK_THUMB} alt={v.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500"/>
                <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/10 transition">
                  <PlayCircle className="w-14 h-14 text-white drop-shadow-lg" />
                </div>
              </div>
              <h3 className="font-serif text-xl">{v.title}</h3>
              <p className="text-white/90 text-sm mt-2 leading-relaxed">{v.excerpt}</p>
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}
